import { SignIn, useAuth, useClerk, useUser } from "@clerk/react";
import { errorCodeOf } from "@luckymenu/api-client";
import { useQuery, useTRPC } from "@luckymenu/api-client/react";
import type { AdminMe } from "@luckymenu/core";
import { legalLinks } from "@luckymenu/core/client";
import { useTranslation } from "@luckymenu/i18n/react";
import {
	AppShell,
	Button,
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
	LegalLinks,
} from "@luckymenu/ui-web";
import { useRouterState } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { env } from "../env";
import { AccountMenu } from "./account-menu";
import { Brand } from "./brand";
import { activeKeyFor, adminNav } from "./nav";
import { RouterLink } from "./router-link";

function Centered({ children }: { children: ReactNode }) {
	return (
		<main className="flex min-h-dvh flex-col items-center justify-center gap-6 bg-surface p-4">
			{children}
		</main>
	);
}

function Loading() {
	const { t } = useTranslation();
	return (
		<Centered>
			<p className="text-sm text-muted-foreground" role="status">
				{t("loading")}
			</p>
		</Centered>
	);
}

function SignInScreen() {
	const { t } = useTranslation("auth");
	const links = legalLinks(env.APP_ENV, env.ROOT_DOMAIN);
	return (
		<Centered>
			<Brand />
			<SignIn routing="hash" />
			<LegalLinks
				links={
					links
						? [
								{ href: links.privacy, label: t("privacy") },
								{ href: links.terms, label: t("terms") },
							]
						: null
				}
			/>
		</Centered>
	);
}

function NotAdmin() {
	const { t } = useTranslation("auth");
	const { signOut } = useClerk();
	const { user } = useUser();
	return (
		<Centered>
			<Brand />
			<Card className="w-full max-w-sm">
				<CardHeader>
					<CardTitle>
						<h1>{t("notAdminTitle")}</h1>
					</CardTitle>
					<CardDescription>{t("notAdminBody")}</CardDescription>
				</CardHeader>
				{user?.primaryEmailAddress ? (
					<CardContent className="text-sm text-muted-foreground">
						{user.primaryEmailAddress.emailAddress}
					</CardContent>
				) : null}
				<CardFooter>
					<Button size="lg" className="w-full" onClick={() => void signOut()}>
						{t("signOut")}
					</Button>
				</CardFooter>
			</Card>
		</Centered>
	);
}

function LoadError({ onRetry }: { onRetry: () => void }) {
	const { t } = useTranslation(["common", "admin"]);
	return (
		<Centered>
			<Brand />
			<p className="text-sm text-muted-foreground" role="alert">
				{t("admin:loadFailed")}
			</p>
			<Button onClick={onRetry}>{t("retry")}</Button>
		</Centered>
	);
}

function AdminShell({ me, children }: { me: AdminMe; children: ReactNode }) {
	const { t } = useTranslation("nav");
	const { t: tAdmin } = useTranslation("admin");
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	return (
		<AppShell
			items={adminNav(t)}
			activeKey={activeKeyFor(pathname)}
			navLabel={t("mainNavigation")}
			brand={<Brand />}
			account={<AccountMenu me={me} />}
			linkComponent={RouterLink}
			variant="sidebar"
			mobileNav="sheet"
			menuLabel={tAdmin("openMenu")}
		>
			{children}
		</AppShell>
	);
}

function SignedInGate({ children }: { children: (me: AdminMe) => ReactNode }) {
	const trpc = useTRPC();
	const me = useQuery(trpc.admin.identity.me.queryOptions());
	if (me.isPending) return <Loading />;
	if (me.error) {
		return errorCodeOf(me.error) === "FORBIDDEN" ? (
			<NotAdmin />
		) : (
			<LoadError onRetry={() => void me.refetch()} />
		);
	}
	return <AdminShell me={me.data}>{children(me.data)}</AdminShell>;
}

/**
 * Admin access: signed out → Clerk sign-in; signed in but not in
 * platform_admins → "Not an admin"; otherwise the shell. The API decides.
 */
export function AdminGate({ children }: { children: (me: AdminMe) => ReactNode }) {
	const { isLoaded, isSignedIn } = useAuth();
	if (!isLoaded) return <Loading />;
	if (!isSignedIn) return <SignInScreen />;
	return <SignedInGate>{children}</SignedInGate>;
}
